import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionTitle } from "./Reveal";
import { TESTIMONIALS } from "@/data/testimonials";
import { INSTAGRAM_URL } from "@/data/site";

const Stars = ({ n }) => (
  <span className="flex gap-0.5" aria-label={`${n} yıldız`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={14} className={i <= n ? "text-[#e6007e] fill-[#e6007e]" : "text-pink-200 fill-pink-100"} />)}
  </span>
);

const Card = ({ t, i }) => (
  <div className="flex-[0_0_88%] sm:flex-[0_0_60%] lg:flex-[0_0_32%] min-w-0 pl-5" data-testid={`testimonial-card-${i}`}>
    <div className="h-full card-soft rounded-[1.75rem] p-7 flex flex-col gap-5 relative overflow-hidden transition-transform duration-500 hover:-translate-y-1">
      <Quote size={56} className="absolute -top-2 right-5 text-[#ffd1dc]/60" />
      <div className="flex items-center justify-between relative"><Stars n={t.rating} /><span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#e6007e] bg-[#fff0f5] rounded-full px-2.5 py-1">{t.service}</span></div>
      <p className="text-sm sm:text-[15px] leading-relaxed text-zinc-700 flex-1 relative">"{t.text}"</p>
      <div className="flex items-center gap-3 relative">
        <span className="w-10 h-10 rounded-full bg-grad-pink text-white text-sm font-semibold flex items-center justify-center shadow-[0_8px_20px_-8px_rgba(230,0,126,0.6)]">{t.name[0]}</span>
        <div><p className="text-sm font-semibold text-[#1a1118]">{t.name}</p><p className="text-xs text-zinc-400">Diva Nail Beauty müşterisi</p></div>
      </div>
    </div>
  </div>
);

export const Testimonials = () => {
  const [ref, embla] = useEmblaCarousel({ loop: true, align: "start", skipSnaps: false });
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);
  const avg = (TESTIMONIALS.reduce((a, t) => a + t.rating, 0) / TESTIMONIALS.length).toFixed(1);

  const onSelect = useCallback(() => embla && setSelected(embla.selectedScrollSnap()), [embla]);
  useEffect(() => { if (!embla) return; embla.on("select", onSelect); onSelect(); }, [embla, onSelect]);
  useEffect(() => {
    if (!embla || paused) return;
    const id = setInterval(() => embla.scrollNext(), 4200);
    return () => clearInterval(id);
  }, [embla, paused]);

  return (
    <section id="yorumlar" className="relative py-28 lg:py-36 bg-grad-blush grain overflow-hidden" data-testid="testimonials-section">
      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(230,0,126,0.14),transparent_60%)] blur-2xl" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionTitle eyebrow="Müşteri Yorumları" title={<>Misafirlerimiz <span className="text-gradient">ne diyor?</span></>} desc="Protez tırnaktan cilt bakımına, Esenyurt'ta bize güvenen misafirlerimizin gerçek deneyimleri." />
          <Reveal delay={0.2}>
            <div className="card-soft rounded-3xl px-6 py-5 flex items-center gap-5" data-testid="testimonials-summary">
              <div><p className="text-4xl font-bold text-gradient leading-none">{avg}</p><Stars n={5} /></div>
              <div className="h-10 w-px bg-pink-200" />
              <div className="text-sm text-zinc-600"><p className="font-semibold text-[#1a1118]">{TESTIMONIALS.length} yorum</p><a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-[#e6007e] hover:underline text-xs">Instagram'da daha fazlası →</a></div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="mt-14">
          <div className="overflow-hidden -ml-5" ref={ref} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="testimonials-slider">
            <div className="flex items-stretch py-2">
              {TESTIMONIALS.map((t, i) => <Card key={i} t={t} i={i} />)}
            </div>
          </div>
          <div className="mt-8 flex items-center justify-between gap-6">
            <div className="flex gap-1.5 flex-wrap max-w-[60%]" data-testid="testimonials-dots">
              {TESTIMONIALS.map((_, i) => (
                <button key={i} onClick={() => embla?.scrollTo(i)} aria-label={`${i + 1}. yorum`} className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${i === selected ? "w-7 bg-grad-pink" : "w-1.5 bg-pink-200 hover:bg-pink-300"}`} />
              ))}
            </div>
            <div className="flex gap-3">
              <motion.button whileTap={{ scale: 0.92 }} onClick={() => embla?.scrollPrev()} data-testid="testimonials-prev" aria-label="Önceki" className="w-12 h-12 rounded-full btn-outline flex items-center justify-center text-[#1a1118]"><ChevronLeft size={20} /></motion.button>
              <motion.button whileTap={{ scale: 0.92 }} onClick={() => embla?.scrollNext()} data-testid="testimonials-next" aria-label="Sonraki" className="w-12 h-12 rounded-full btn-grad-dark flex items-center justify-center text-white"><ChevronRight size={20} /></motion.button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
